<script setup lang="ts">
import { useSeo } from '@/composables/useSeo'
import PageLayout from '@/components/layout/PageLayout.vue'
import Card from '@/components/ui/Card.vue'
import Icon from '@/components/icons/Icon.vue'

useSeo({
  title: 'Calculator Methodology',
  description:
    'How ClearRand builds, verifies and documents its South African calculator formulas and assumptions.',
  path: '/methodology',
})

const steps = [
  {
    step: '1',
    icon: 'calculator' as const,
    title: 'Define',
    description: 'Inputs, validation rules and result formats are written as typed definitions.',
  },
  {
    step: '2',
    icon: 'check' as const,
    title: 'Verify',
    description: 'Every formula is unit-tested against known examples and boundary cases.',
  },
  {
    step: '3',
    icon: 'info' as const,
    title: 'Document',
    description: 'Sources, assumptions, effective dates and limitations are shown on each page.',
  },
]
</script>

<template>
  <PageLayout
    title="Methodology"
    description="How we build, document and verify every calculator."
    :breadcrumbs="[{ label: 'Methodology' }]"
  >
    <div class="section-heading">
      <span class="section-heading__eyebrow">Our process</span>
      <h2 class="section-heading__title">How we build every calculator</h2>
    </div>
    <div class="method-grid">
      <Card v-for="item in steps" :key="item.step" padding="lg" raised accent="accent" hover>
        <div class="method-card__header">
          <span class="method-card__step">{{ item.step }}</span>
          <div class="method-card__icon">
            <Icon :name="item.icon" :size="20" />
          </div>
        </div>
        <h2 class="method-card__title">{{ item.title }}</h2>
        <p class="method-card__desc">{{ item.description }}</p>
      </Card>
    </div>

    <article class="prose">
      <p>
        Each calculator is built from a typed definition that separates inputs, validation, the
        calculation itself, and result presentation. This keeps the logic testable, readable and
        independent of the UI.
      </p>
      <h2>Data and sources</h2>
      <ul>
        <li>Tax rates, rebates and thresholds come from official SARS publications.</li>
        <li>Fuel prices are sourced from the Department of Mineral Resources and Energy.</li>
        <li>Transfer duty and VAT rates come from SARS.</li>
        <li>Solar, vehicle and property assumptions are clearly marked as estimates.</li>
      </ul>
      <h2>Verification</h2>
      <p>
        We unit-test every formula and boundary condition. When authoritative rules cannot be
        represented faithfully, we narrow the calculator's claim and show the limitation instead of
        guessing.
      </p>
      <h2>Updates</h2>
      <p>
        Data changes are released as new versions with a clear effective date. Each calculator page
        shows the last reviewed date and the version of data it is using.
      </p>
    </article>
  </PageLayout>
</template>

<style scoped>
.section-heading {
  margin-bottom: var(--space-5);
}

.section-heading__eyebrow {
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-primary);
}

.section-heading__title {
  font-size: var(--text-2xl);
  font-weight: var(--font-bold);
  margin: 0;
}

.method-grid {
  display: grid;
  gap: var(--space-4);
  grid-template-columns: 1fr;
  margin-bottom: var(--space-10);
}

@media (min-width: 640px) {
  .method-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.method-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-4);
}

.method-card__step {
  font-size: var(--text-3xl);
  font-weight: var(--font-extrabold);
  color: var(--color-border-strong);
  line-height: 1;
}

.method-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  background: linear-gradient(135deg, rgb(217 119 6 / 0.12), rgb(251 191 36 / 0.08));
  color: var(--color-accent-dark);
}

.method-card__title {
  font-size: var(--text-xl);
  font-weight: var(--font-bold);
  color: var(--color-text);
}

.method-card__desc {
  margin-top: var(--space-2);
  color: var(--color-text-muted);
  font-size: var(--text-base);
  line-height: var(--leading-relaxed);
}

.prose {
  max-width: 70ch;
  color: var(--color-text);
  line-height: var(--leading-relaxed);
}

.prose h2 {
  margin-top: var(--space-8);
  font-size: var(--text-xl);
  font-weight: var(--font-semibold);
}

.prose p,
.prose ul {
  margin-top: var(--space-3);
}

.prose ul {
  padding-left: var(--space-5);
}
</style>
